import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Sparkles, Wand2, Gamepad2, Newspaper, Plus, Loader2,
  Clock, Users, Trophy
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const AICreatorPage = () => {
  const { user, token } = useContext(UserContext);
  const [gamePrompt, setGamePrompt] = useState("");
  const [betPrompt, setBetPrompt] = useState({
    article_title: "",
    bet_description: "",
    end_date: "",
    options: ["Yes", "No"]
  });
  const [loadingGame, setLoadingGame] = useState(false);
  const [loadingBet, setLoadingBet] = useState(false);
  const [aiGames, setAiGames] = useState([]);
  const [createdGame, setCreatedGame] = useState(null);
  const [createdMarket, setCreatedMarket] = useState(null);

  useEffect(() => {
    fetchAIGames();
  }, []);

  const fetchAIGames = async () => {
    try {
      const response = await axios.get(`${API}/ai/games`);
      setAiGames(response.data.games || []);
    } catch (error) {
      console.error("Fetch AI games error:", error);
    }
  };

  const createGame = async () => {
    if (!gamePrompt.trim()) {
      toast.error("Please describe your game idea");
      return;
    }

    setLoadingGame(true);
    setCreatedGame(null);
    
    try {
      const response = await axios.post(`${API}/ai/create-game`, {
        prompt: gamePrompt
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedGame(response.data.game);
      toast.success("Game created successfully!");
      fetchAIGames();
      setGamePrompt("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create game");
    } finally {
      setLoadingGame(false);
    }
  };

  const createBet = async () => {
    if (!betPrompt.article_title || !betPrompt.bet_description || !betPrompt.end_date) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoadingBet(true);
    setCreatedMarket(null);
    
    try {
      const response = await axios.post(`${API}/ai/create-bet`, {
        ...betPrompt,
        end_date: new Date(betPrompt.end_date).toISOString()
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedMarket(response.data.market);
      toast.success("Betting market created!");
      setBetPrompt({ article_title: "", bet_description: "", end_date: "", options: ["Yes", "No"] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create market");
    } finally {
      setLoadingBet(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="ai-creator-title">
              AI Creator
            </h1>
            <p className="text-[#A1A1AA] text-sm">Create custom games and betting markets with Claude AI</p>
          </div>
        </div>

        <Tabs defaultValue="games" className="space-y-6">
          <TabsList className="bg-[#1A1B1F] border border-white/[0.08]">
            <TabsTrigger value="games" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              <Gamepad2 className="w-4 h-4 mr-2" /> Create Game
            </TabsTrigger>
            <TabsTrigger value="bets" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              <Newspaper className="w-4 h-4 mr-2" /> Create Bet
            </TabsTrigger>
            <TabsTrigger value="library" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              <Trophy className="w-4 h-4 mr-2" /> Game Library
            </TabsTrigger>
          </TabsList>

          {/* Create Game Tab */}
          <TabsContent value="games">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-[#00F0FF]" />
                  Describe Your Game
                </h3>
                
                <Textarea
                  value={gamePrompt}
                  onChange={(e) => setGamePrompt(e.target.value)}
                  placeholder="Describe your game idea... e.g., 'A game where players bet on which color ball will be drawn from a bag. There are 5 red balls and 3 blue balls. Red pays 1.5x, blue pays 2.5x'"
                  className="min-h-[200px] bg-black/20 border-white/10 text-white placeholder:text-[#52525B] mb-4"
                  data-testid="game-prompt-input"
                />
                
                <div className="space-y-2 mb-4">
                  <p className="text-[#A1A1AA] text-sm">Example prompts:</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Coin flip with custom odds",
                      "Rock paper scissors betting",
                      "Number guessing game",
                      "Card draw prediction"
                    ].map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setGamePrompt(prompt)}
                        className="px-3 py-1 rounded-full text-xs bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={createGame}
                  disabled={loadingGame || !gamePrompt.trim()}
                  className="w-full h-12 bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold"
                  data-testid="create-game-btn"
                >
                  {loadingGame ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Game
                    </>
                  )}
                </Button>
              </div>

              {/* Output */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">
                  Generated Game
                </h3>
                
                {createdGame ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                      <h4 className="text-xl font-bold text-white mb-2">{createdGame.name}</h4>
                      <p className="text-[#A1A1AA] text-sm mb-4">{createdGame.description}</p>
                      
                      <div className="space-y-3">
                        <div>
                          <span className="text-[#52525B] text-xs uppercase">Rules</span>
                          <ul className="mt-1 space-y-1">
                            {createdGame.rules?.map((rule, idx) => (
                              <li key={idx} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                                <span className="text-[#00F0FF]">•</span>
                                {rule}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <span className="text-[#52525B] text-xs uppercase">Win Condition</span>
                          <p className="text-white text-sm mt-1">{createdGame.win_condition}</p>
                        </div>
                        
                        <div className="flex gap-4">
                          <div>
                            <span className="text-[#52525B] text-xs uppercase">Type</span>
                            <p className="text-white text-sm mt-1 capitalize">{createdGame.game_type}</p>
                          </div>
                          <div>
                            <span className="text-[#52525B] text-xs uppercase">Multiplier Range</span>
                            <p className="text-[#00E701] text-sm mt-1 font-mono">
                              {createdGame.multiplier_range?.join('x - ')}x
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-[#52525B] text-xs text-center">
                      Game saved to your library! Custom games will be available for play soon.
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-center py-12">
                    <div>
                      <Sparkles className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                      <p className="text-[#52525B]">Your AI-generated game will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Create Bet Tab */}
          <TabsContent value="bets">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4 flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-[#00E701]" />
                  Create Betting Market
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Article/Topic Title</label>
                    <Input
                      value={betPrompt.article_title}
                      onChange={(e) => setBetPrompt({ ...betPrompt, article_title: e.target.value })}
                      placeholder="e.g., Tesla Q1 2026 Earnings Report"
                      className="bg-black/20 border-white/10 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">What do you want to bet on?</label>
                    <Textarea
                      value={betPrompt.bet_description}
                      onChange={(e) => setBetPrompt({ ...betPrompt, bet_description: e.target.value })}
                      placeholder="e.g., Will Tesla beat earnings expectations?"
                      className="min-h-[100px] bg-black/20 border-white/10 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">End Date</label>
                    <Input
                      type="datetime-local"
                      value={betPrompt.end_date}
                      onChange={(e) => setBetPrompt({ ...betPrompt, end_date: e.target.value })}
                      className="bg-black/20 border-white/10 text-white"
                    />
                  </div>
                  
                  <Button
                    onClick={createBet}
                    disabled={loadingBet}
                    className="w-full h-12 bg-[#00E701] hover:bg-[#00C201] text-black font-bold"
                    data-testid="create-bet-btn"
                  >
                    {loadingBet ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating with AI...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Market
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Output */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">
                  Generated Market
                </h3>
                
                {createdMarket ? (
                  <div className="p-4 rounded-lg bg-[#00E701]/10 border border-[#00E701]/20">
                    <span className="px-2 py-1 rounded text-xs bg-[#00E701]/20 text-[#00E701]">
                      {createdMarket.category}
                    </span>
                    <h4 className="text-xl font-bold text-white mt-3 mb-2">{createdMarket.title}</h4>
                    <p className="text-[#A1A1AA] text-sm mb-4">{createdMarket.description}</p>
                    
                    <div className="flex items-center gap-2 text-[#52525B] text-sm">
                      <Clock className="w-4 h-4" />
                      Ends {new Date(createdMarket.end_date).toLocaleDateString()}
                    </div>
                    
                    <div className="mt-4 p-3 rounded bg-white/5">
                      <p className="text-[#A1A1AA] text-xs">Market ID: {createdMarket.market_id}</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-center py-12">
                    <div>
                      <Newspaper className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                      <p className="text-[#52525B]">Your AI-generated market will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Game Library Tab */}
          <TabsContent value="library">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aiGames.length > 0 ? aiGames.map((game, idx) => (
                <div 
                  key={idx}
                  className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08] hover:border-[#00F0FF]/30 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      game.game_type === 'skill' ? 'bg-[#007AFF]/20 text-[#007AFF]' :
                      game.game_type === 'luck' ? 'bg-[#FF007A]/20 text-[#FF007A]' :
                      'bg-[#FFD600]/20 text-[#FFD600]'
                    }`}>
                      {game.game_type}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{game.name}</h4>
                  <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2">{game.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-[#00E701] font-mono text-sm">
                      {game.multiplier_range?.join('-')}x
                    </span>
                    <Button variant="outline" size="sm" className="border-white/10 text-white/60 hover:text-white">
                      Coming Soon
                    </Button>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-12">
                  <Gamepad2 className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No custom games yet. Create your first one!</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
